import { Link } from '@tanstack/react-router';
import {
  CheckIcon,
  EyeIcon,
  ListChecksIcon,
  MoreHorizontalIcon,
  NetworkIcon,
  PencilIcon,
  PlusIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useBoardSelectionStore } from '../boardSelectionStore';

/**
 * The board header's controls on a phone (spec 262).
 *
 * Select first: there is no Shift and no hover on a phone, so the way to pick
 * cards is a mode, and it says Done while it is on. Then New task, the
 * board's one primary. Show diagram and Edit workflow, which a desktop shows
 * as two buttons, go behind ⋯: on a 390px band they were a second and third
 * row before any card.
 */
export function BoardPhoneActions({
  workspaceId,
  workflowKey,
  canWork,
  showDiagram,
  onShowDiagramChange,
  onCreate,
}: {
  workspaceId: string;
  workflowKey: string;
  canWork: boolean;
  showDiagram: boolean;
  onShowDiagramChange?: (show: boolean) => void;
  onCreate: () => void;
}) {
  const picking = useBoardSelectionStore((state) => state.picking);
  const selected = useBoardSelectionStore((state) => state.taskIds.length);
  const setPicking = useBoardSelectionStore((state) => state.setPicking);
  const on = picking || selected > 0;

  return (
    // The row's width, Select and New task sharing it and ⋯ at 44px
    // (spec 269): at their own widths they sat at the band's start.
    <div className="flex w-full items-center gap-2">
      {canWork ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="flex-1"
          aria-pressed={on}
          onClick={() => {
            setPicking(!on);
          }}
        >
          {on ? (
            <>
              <CheckIcon aria-hidden="true" />
              Done
            </>
          ) : (
            <>
              <ListChecksIcon aria-hidden="true" />
              Select
            </>
          )}
        </Button>
      ) : null}
      {canWork ? (
        <Button type="button" size="sm" className="flex-1" onClick={onCreate}>
          <PlusIcon aria-hidden="true" />
          New task
        </Button>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              size="icon-sm"
              variant="outline"
              aria-label="More board actions"
            />
          }
        >
          <MoreHorizontalIcon aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              onShowDiagramChange?.(!showDiagram);
            }}
          >
            <NetworkIcon aria-hidden="true" />
            {showDiagram ? 'Hide diagram' : 'Show diagram'}
          </DropdownMenuItem>
          <DropdownMenuItem
            render={
              <Link
                to="/workspaces/$workspaceId/workflows/$workflowKey/builder"
                params={{ workspaceId, workflowKey }}
              />
            }
          >
            {canWork ? (
              <>
                <PencilIcon aria-hidden="true" />
                Edit workflow
              </>
            ) : (
              <>
                <EyeIcon aria-hidden="true" />
                View workflow
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
